import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';

import { CascaderComponents } from '../index';
import type {
  CascaderOption,
  CascaderComponentTypes,
  CascaderComponentName,
} from '../types';

export interface CascaderComponentsContextValue<
  OptionType extends CascaderOption,
> {
  getComponent: <T extends CascaderComponentName>(
    componentName: T,
  ) => CascaderComponentTypes<OptionType>[T];
}

const CascaderComponentsContext =
  createContext<CascaderComponentsContextValue<CascaderOption> | null>(null);

export interface CascaderComponentsProviderProps<
  OptionType extends CascaderOption,
> {
  children: ReactNode;
  components?: Partial<CascaderComponentTypes<OptionType>>;
}

/**
 * CascaderComponentsProvider is responsible for combining the default components with user-defined
 * custom components, and providing an interface to subcomponent wrappers to access those user-defined components.
 */
export const CascaderComponentsProvider = <OptionType extends CascaderOption>({
  children,
  components,
}: CascaderComponentsProviderProps<OptionType>) => {
  const combinedComponents = useMemo(
    () => ({
      ...CascaderComponents,
      ...components,
    }),
    [components],
  );

  const getComponent = useCallback(
    (componentName: CascaderComponentName) => combinedComponents[componentName],
    [combinedComponents],
  );

  // NB: Without type coercion, this produces a union type that is too complex to represent
  const contextValue = useMemo(
    () => ({
      getComponent,
    }),
    [getComponent],
  ) as CascaderComponentsContextValue<OptionType>;

  return (
    <CascaderComponentsContext.Provider value={contextValue}>
      {children}
    </CascaderComponentsContext.Provider>
  );
};

export const useCascaderComponentsContext = <
  OptionType extends CascaderOption,
>(): CascaderComponentsContextValue<OptionType> => {
  const context = useContext(
    CascaderComponentsContext,
  ) as CascaderComponentsContextValue<OptionType>;
  if (!context) {
    throw new Error(
      'useCascaderComponentsContext must be used within a CascaderComponentsProvider',
    );
  }
  return context;
};
